import React, { useEffect, useState } from 'react';
import { Typography, Tag, Spin, Tooltip, message, Table, Button, Modal, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, PrinterOutlined, CarOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { PDFDocument, type PDFPage } from 'pdf-lib';
import { getOrderTracks, getTpLabelUrl, getSelfShippingBrands } from 'services/ordersApi';
import { useUpdateTrackingPrint } from 'hooks/useOrders';
import PdfViewerModal from 'components/PrintShippingLabel/PdfViewerModal';
import { createShippingLabelPdfBase64, type ShippingLabelData } from 'components/PrintShippingLabel';
import SelfTrackingForm from 'components/OrderManage/OrderPrepareShip/SelfTrackingForm';
import {
  type SelfShippingBrand,
  getSelfShippingBrandKey,
  mapSelfShippingBrands,
  piecesToTrackingNumbers,
} from 'components/OrderManage/OrderPrepareShip/selfTracking';

interface TrackingNumberPanelProps {
  /** order_id ที่ต้องการแสดง Tracking */
  orderId: string;
  /** สถานะการเปิดของ Modal เพื่อดึงข้อมูลใหม่ทุกครั้งที่เปิด */
  isOpen?: boolean;
  /** ส่งกลับไปแจ้งว่ารายการมี Tracking ครบทุกอันหรือไม่ และ Print ครบหรือยัง */
  onTrackingStatusChange?: (isComplete: boolean, isPrinted: boolean) => void;
  /** true = ซ่อนคอลัมน์สถานะพิมพ์ + ปุ่มพิมพ์ (ใช้กับแถบเครดิตรอชำระ/เสร็จสิ้น/ยกเลิก ที่ไม่ต้องพิมพ์ label แล้ว) เหลือแค่ปุ่ม copy */
  hidePrintControls?: boolean;
  /** true = ไม่แสดง "ยังไม่มีข้อมูลเลขพัสดุ" (ผู้เรียกแสดง empty state ของตัวเองแทน) */
  hideEmptyState?: boolean;
  /** true = ส่งเองที่ยังไม่กรอกเลขจริง แสดงแค่รหัส BB ไม่ต้องมี "กำลังจัดส่ง :" นำหน้า (แถบเตรียมจัดส่ง/ระหว่างจัดส่ง) */
  bbCodeOnly?: boolean;
  /** true = แก้เลขพัสดุ/บริษัทขนส่งของ "ส่งเอง" ที่กรอกเลขจริงแล้วได้ (แถบระหว่างจัดส่ง) */
  allowEditSelfTracking?: boolean;
}

/** แต่ละแถวในตาราง */
interface TrackingRow {
  key: string;
  tracking_id: string;
  retail_order_item_id: string | null;  // ใช้เรียงลำดับให้ตรงกับรายการสินค้า
  /** ลำดับกล่องในรายการ (1..qty) — backend จับคู่เลขพัสดุส่งเองตามลำดับนี้ */
  item_no: string | null;
  product_name: string;
  sku_description: string | null;
  order_code: string | null;
  factory_name: string | null;
  factory_code: string | null;
  /** เบอร์โทรผู้ประกอบการ — ใช้แสดงบน label ส่งเอง */
  factory_tel: string | null;
  brand_logo: string | null;
  is_self: boolean;
  carrier_code: string | null;
  tracking_number: string | null;
  image_url: string | null;
  is_print: boolean;
  self_transport_brand_id: string | null;
  self_tracking_number: string | null;
  self_brand_name: string | null;
  self_brand_logo: string | null;
  /** สถานะการจัดส่งของ tracking แถวนี้ (SHIPPING_ACTIVITY_STATUS: on_delivery/delivered/...) — ใช้เลือกสี Tag */
  status: string | null;
  /** ข้อความสถานะภาษาไทยแบบเดียวกับระบบ timeline (carrier-aware) — ใช้แสดงผลจริง แทน status ตรงๆ */
  status_label: string | null;
  /** payment_type ของออเดอร์ (ใช้เช็คว่าเป็น COD ไหม) */
  payment_type: string | null;
  /** true = tracking แถวนี้เคยได้รับ webhook '9'/'48' แล้ว (COD เก็บเงินสำเร็จ) */
  is_cod_settled: boolean;
  // Address info
  address_name: string | null;
  address_telephone: string | null;
  address_text: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  province_name: string | null;
  district_name: string | null;
  sub_district_name: string | null;
  zipcode: string | null;
}

const MAX_MERGE = 100;

/** สถานะการจัดส่ง (SHIPPING_ACTIVITY_STATUS) — คำ/สี เดียวกับที่ ParcelListTab.tsx ใช้ ให้สม่ำเสมอกัน */
// สีข้อความ (ไม่ใช่ badge) ของสถานะการจัดส่ง — ใช้ hex ตรงของ antd preset color ตัวเดิม
// ('active' = placeholder ที่ backend เขียนตอนสร้าง label/จองขนส่งครั้งแรก ไม่ใช่จาก webhook จริง
// ใส่สีเดียวกับ on_delivery เพราะความหมายใกล้เคียงกัน)
const SHIPPING_STATUS_COLOR: Record<string, string> = {
  active: '#1890ff',
  waiting: '#fa8c16',
  on_delivery: '#1890ff',
  delivered: '#52c41a',
  canceled: '#999999',
  on_return: '#fa541c',
  returned: '#722ed1',
  claimed: '#faad14',
};
const SHIPPING_STATUS_LABEL_TH: Record<string, string> = {
  active: 'กำลังจัดส่ง',
  waiting: 'รอขนส่งเข้ารับ',
  on_delivery: 'กำลังจัดส่ง',
  delivered: 'จัดส่งสำเร็จ',
  canceled: 'ยกเลิกแล้ว',
  on_return: 'กำลังส่งคืน',
  returned: 'ส่งคืนแล้ว',
  claimed: 'เคลมแล้ว',
};

const isValidPdfUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const t = url.trim();
  return t.startsWith('http://') || t.startsWith('https://');
};

/**
 * TrackingNumberPanel
 * แสดงเลขพัสดุ: Logo | Tracking | Copy | Print
 * รองรับ Print 3 ระดับ: ทั้ง Order / รายละเอียด SKU / ราย Tracking
 */
const TrackingNumberPanel: React.FC<TrackingNumberPanelProps> = ({ orderId, isOpen, onTrackingStatusChange, hidePrintControls, hideEmptyState, bbCodeOnly, allowEditSelfTracking }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TrackingRow[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);

  // PDF Preview state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pendingPrintKeys, setPendingPrintKeys] = useState<string[]>([]);

  // แก้ไขเลขพัสดุส่งเอง (retail_order_item_id ที่กำลังแก้)
  const [editSelfItemId, setEditSelfItemId] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [selfBrands, setSelfBrands] = useState<SelfShippingBrand[]>([]);

  const { mutateAsync: updateTrackingPrint } = useUpdateTrackingPrint();

  const fetchData = () => {
    if (!orderId) return;
    setLoading(true);
    getOrderTracks({ order_ids: [orderId] })
      .then((res) => {
        const data = res?.data ?? [];
        console.log("data",res);
        const mapped: TrackingRow[] = data.map((row) => ({
          key: row.tracking?.id ?? Math.random().toString(),
          tracking_id: row.tracking?.id ?? '',
          retail_order_item_id: row.tracking?.retail_order_item_id ?? null,
          item_no: row.tracking?.item_no ?? null,
          product_name: row.item?.product_name ?? '-',
          sku_description: row.item?.sku_description ?? null,
          order_code: row.item?.order_code ?? null,
          factory_name: row.order_item?.factory_name ?? null,
          factory_code: row.order_item?.factory_code ?? null,
          factory_tel: row.order_item?.factory_tel ?? null,
          brand_logo: row.tracking?.brand_logo?.replace(/_SPEED/gi, '') ?? null,
          is_self: row.tracking?.shipment_type === 'self',
          carrier_code: row.tracking?.carrier_code ?? null,
          tracking_number: row.tracking?.tracking_number ?? null,
          image_url: row.tracking?.image_url ?? null,
          is_print: row.tracking?.is_print ?? false,
          self_transport_brand_id: row.tracking?.self_transport_brand_id ?? null,
          self_tracking_number: row.tracking?.self_tracking_number ?? null,
          self_brand_name: row.tracking?.self_brand_name ?? null,
          self_brand_logo: row.tracking?.self_brand_logo ?? null,
          status: row.tracking?.status ?? null,
          status_label: row.tracking?.status_label ?? null,
          payment_type: row.tracking?.payment_type ?? null,
          is_cod_settled: !!row.tracking?.is_cod_settled,
          address_name: row.address?.address_name ?? null,
          address_telephone: row.address?.address_telephone ?? null,
          address_text: row.address?.address_text ?? null,
          address_line_1: row.address?.address_line_1 ?? null,
          address_line_2: row.address?.address_line_2 ?? null,
          province_name: row.address?.province_name ?? null,
          district_name: row.address?.district_name ?? null,
          sub_district_name: row.address?.sub_district_name ?? null,
          zipcode: row.address?.zipcode ? String(row.address.zipcode) : null,
        }));
        // เรียงตาม retail_order_item_id ASC เพื่อให้ตรงกับลำดับรายการสินค้าใน ModalOrderDetail
        mapped.sort((a, b) => {
          if (!a.retail_order_item_id) return 1;
          if (!b.retail_order_item_id) return -1;
          return a.retail_order_item_id.localeCompare(b.retail_order_item_id);
        });
        setRows(mapped);
        
        // เช็คว่า Tracking ครบสมบูรณ์หรือไม่ และพิมพ์ครบทุกอันหรือยัง
        if (onTrackingStatusChange && mapped.length > 0) {
          const isComplete = mapped.every((row) => {
            if (row.is_self) {
              return !!row.self_tracking_number; // ส่งเองต้องมีเลขที่กรอก
            }
            return !!row.tracking_number; // ส่งกับ BB/Speed ต้องมีเลขระบบ
          });
          
          // ดึงรายการที่พร้อมพิมพ์ (ตรงกับ canPrint — ส่งเองพิมพ์ได้เฉพาะก่อนกรอกเลขพัสดุจริง
          // ด้วยรหัส BB ที่ระบบออกให้อัตโนมัติ, กรอกเลขจริงแล้วถือว่าจบขั้นตอน label ของเรา)
          const printableRows = mapped.filter((row) => {
            if (row.is_self) return !row.self_tracking_number && !!row.tracking_number;
            return !!row.tracking_number;
          });
          const isPrinted = printableRows.length === 0 || printableRows.every((row) => row.is_print);
          onTrackingStatusChange(isComplete, isPrinted);
        }

      })
      .catch((err) => console.error('TrackingNumberPanel fetch error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen || !orderId) return; // หยุดถ้า Modal ปิดหรือยังไม่มี orderId

    fetchData(); // ดึงข้อมูลทันทีเมื่อ Modal เปิด

    const handleForceRefresh = () => fetchData();
    window.addEventListener('forceRefreshTrackingPanel', handleForceRefresh);

    return () => {
      window.removeEventListener('forceRefreshTrackingPanel', handleForceRefresh);
    };
  }, [orderId, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => message.success('คัดลอกแล้ว'))
      .catch(() => message.error('ไม่สามารถคัดลอกได้'));
  };

  const openEditSelf = (retailOrderItemId: string) => {
    setEditSelfItemId(retailOrderItemId);
    if (selfBrands.length === 0) {
      getSelfShippingBrands()
        .then((res: any) => setSelfBrands(mapSelfShippingBrands(res)))
        .catch(() => message.error('โหลดรายชื่อบริษัทขนส่งไม่สำเร็จ'));
    }
  };

  // ทุกกล่องของรายการที่กำลังแก้ เรียงตาม item_no (backend บันทึกเลขทับทุกกล่องของรายการพร้อมกัน)
  const editPieces = editSelfItemId
    ? rows
        .filter((r) => r.retail_order_item_id === editSelfItemId)
        .sort((a, b) => Number(a.item_no) - Number(b.item_no))
    : [];
  const editFirst = editPieces[0];

  // ---- PDF helpers ----
  const buildSelfLabel = (row: TrackingRow): ShippingLabelData => {
    const isBkk = row.province_name?.includes('กรุงเทพ');
    const addPrefix = (val: string | null | undefined, prefix: string) => {
      if (!val) return undefined;
      if (/^(ที่อยู่|แขวง|เขต|ต\.|อ\.|จ\.|ตำบล|อำเภอ|จังหวัด)/.test(val)) return val;
      return `${prefix}${val}`;
    };

    return {
      factoryName: row.factory_name ?? undefined,
      factoryCode: row.factory_code ?? undefined,
      factoryPhone: row.factory_tel ?? undefined,
      orderCode: row.order_code ?? '',
      trackingNumber: row.tracking_number ?? undefined,
      productName: row.product_name,
      productSku: row.sku_description ?? undefined,
      recipientName: row.address_name ?? undefined,
      recipientPhone: row.address_telephone ?? undefined,
      addressLine1: addPrefix(
        [row.address_line_1, row.address_line_2].filter(Boolean).join(' ') || row.address_text || undefined, 
        'ที่อยู่ '
      ),
      addressLine2: [
        addPrefix(row.sub_district_name, isBkk ? 'แขวง ' : 'ตำบล '),
        addPrefix(row.district_name, isBkk ? 'เขต ' : 'อำเภอ '),
      ].filter(Boolean).join(' ') || undefined,
      addressLine4: [
        addPrefix(row.province_name, isBkk ? '' : 'จังหวัด '), 
        row.zipcode
      ].filter(Boolean).join(' ') || undefined,
    };
  };

  const openPdfPreview = (blobUrl: string, title: string, keys: string[]) => {
    setPdfUrl(blobUrl);
    setPdfTitle(title);
    setPendingPrintKeys(keys);
    setPdfOpen(true);
  };

  const closePdfPreview = () => {
    if (pdfUrl?.startsWith('blob:')) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfOpen(false);
    setPendingPrintKeys([]);
  };

  const afterPrint = () => {
    if (!pendingPrintKeys.length) return;
    updateTrackingPrint({ tracking_ids: pendingPrintKeys })
      .then(() => {
        //message.success(`มาร์กพิมพ์แล้ว ${pendingPrintKeys.length} รายการ`);
        setSelectedKeys([]);
        fetchData();
        window.dispatchEvent(new Event('forceRefreshOrderTable'));
      })
      .catch(() => message.warning('ส่งพิมพ์แล้ว แต่มาร์กสถานะไม่สำเร็จ'));
  };

  /** merge หลาย PDF (bangbow) จาก image_url */
  const mergePdfFromUrls = async (urls: string[]): Promise<Blob> => {
    const buffers = await Promise.all(urls.map(async (url) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`โหลด PDF ไม่สำเร็จ: ${url}`);
      return res.arrayBuffer();
    }));
    const merged = await PDFDocument.create();
    for (const buf of buffers) {
      const pdf = await PDFDocument.load(buf);
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p: PDFPage) => merged.addPage(p));
    }
    const bytes = await merged.save();
    return new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  };

  /** merge หลาย PDF (self) จาก createShippingLabelPdfBase64 */
  const mergePdfFromSelf = async (targetRows: TrackingRow[]): Promise<Blob> => {
    const base64List = await Promise.all(targetRows.map((r) => createShippingLabelPdfBase64(buildSelfLabel(r))));
    const merged = await PDFDocument.create();
    for (const b64 of base64List) {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const pdf = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p: PDFPage) => merged.addPage(p));
    }
    const bytes = await merged.save();
    return new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  };

  /** Print rows ที่ส่งมา (รับทั้ง bangbow และ self แบบ mixed) */
  const doPrint = async (targetRows: TrackingRow[], title: string) => {
    if (!targetRows.length) { message.warning('ไม่มีรายการที่เลือก'); return; }
    if (targetRows.length > MAX_MERGE) { message.warning(`รวมได้สูงสุด ${MAX_MERGE} รายการ`); return; }

    setMergeLoading(true);
    try {
      // resolve THAIPOST rows ที่ยังไม่มี image_url ก่อน merge
      const resolvedRows: TrackingRow[] = await Promise.all(
        targetRows.map(async (r) => {
          if (!r.is_self && !r.image_url && r.carrier_code === 'THAIPOST' && r.tracking_number) {
            try {
              const result = await getTpLabelUrl(r.tracking_number);
              return { ...r, image_url: result.image_url };
            } catch {
              return r;
            }
          }
          return r;
        })
      );

      const selfRows = resolvedRows.filter((r) => r.is_self);
      const bangbowRows = resolvedRows.filter((r) => !r.is_self && isValidPdfUrl(r.image_url));

      let blob: Blob;

      if (selfRows.length && !bangbowRows.length) {
        blob = await mergePdfFromSelf(selfRows);
      } else if (!selfRows.length && bangbowRows.length) {
        blob = await mergePdfFromUrls(bangbowRows.map((r) => r.image_url!));
      } else {
        // mixed: bangbow แล้ว self ต่อท้าย
        const bangbowBlob = bangbowRows.length ? await mergePdfFromUrls(bangbowRows.map((r) => r.image_url!)) : null;
        const selfBlob = selfRows.length ? await mergePdfFromSelf(selfRows) : null;
        const merged = await PDFDocument.create();
        for (const b of [bangbowBlob, selfBlob]) {
          if (!b) continue;
          const arr = await b.arrayBuffer();
          const pdf = await PDFDocument.load(arr);
          const pages = await merged.copyPages(pdf, pdf.getPageIndices());
          pages.forEach((p: PDFPage) => merged.addPage(p));
        }
        const bytes = await merged.save();
        blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      }

      const blobUrl = URL.createObjectURL(blob);
      openPdfPreview(blobUrl, title, targetRows.map((r) => r.tracking_id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'สร้าง PDF ไม่สำเร็จ';
      message.error(msg);
    } finally {
      setMergeLoading(false);
    }
  };

  // ---- Print actions ----
  // ส่งเองพิมพ์ได้ด้วยรหัส BB ที่ระบบออกให้อัตโนมัติ เฉพาะ "ก่อน" กรอกเลขพัสดุจริงจากไปรษณีย์เท่านั้น
  // — พอกรอกเลขจริงแล้ว (self_tracking_number) ถือว่าจบขั้นตอน label ของเรา ไม่ต้องพิมพ์ซ้ำอีก
  const canPrint = (r: TrackingRow) =>
    r.is_self ? (!r.self_tracking_number && !!r.tracking_number) : !!r.tracking_number;
  const printAll = () => doPrint(rows.filter(canPrint), `Order ทั้งหมด (${rows.length} Label)`);
  const printBySku = (skuKey: string, skuRows: TrackingRow[]) => doPrint(skuRows.filter(canPrint), `SKU: ${skuKey}`);
  const printSingle = (row: TrackingRow) => doPrint([row], row.tracking_number ?? 'Label');
  const printSelected = () => {
    const target = rows.filter((r) => selectedKeys.includes(r.key) && canPrint(r));
    doPrint(target, `Label ที่เลือก (${target.length} รายการ)`);
  };

  // คอลัมน์ checkbox/โลโก้ ชิดบน ให้ตรงบรรทัดเลขพัสดุ
  const topAlignCell = () => ({ style: { verticalAlign: 'top' as const } });

  const columns: ColumnsType<TrackingRow> = [
    // checkbox นี้ใช้เลือกสำหรับพิมพ์หลายรายการเท่านั้น — ซ่อนคู่กับสถานะพิมพ์/ปุ่มพิมพ์เมื่อ hidePrintControls
    ...(hidePrintControls ? [] : [{
      title: '',
      width: 32,
      align: 'center' as const,
      onCell: topAlignCell,
      render: (_: any, record: TrackingRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 22 }}>
          <Checkbox
            checked={selectedKeys.includes(record.key)}
            disabled={!canPrint(record)}
            onChange={(e) => {
              setSelectedKeys((prev) =>
                e.target.checked ? [...prev, record.key] : prev.filter((k) => k !== record.key)
              );
            }}
          />
        </div>
      ),
    }]),
    {
      title: 'ขนส่ง',
      dataIndex: 'brand_logo',
      width: 50,
      align: 'center',
      onCell: topAlignCell,
      render: (_logo, record) =>
        record.is_self ? (
          record.self_brand_logo && record.self_brand_logo != '' ? 
          <>
          <div ><img src={record.self_brand_logo} alt="" style={{ height: 26, objectFit: 'contain' }} /></div>
{/*<Tag style={{ fontSize: 8, margin: 0 }}>ส่งเอง</Tag>*/ }         
          </>
          : <Tag style={{ fontSize: 10, margin: 0 }}>ส่งเอง</Tag>
        ) : record.brand_logo ? (
          <img src={record.brand_logo} alt="" style={{ height: 28, objectFit: 'contain' }} />
        ) : (
          <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>🚚</Tag>
        ),
    },
    {
      // คอลัมน์เดียว: บรรทัดบน = เลขพัสดุ + สถานะพิมพ์/ปุ่ม (ชิดขวา)
      // บรรทัดล่าง = สถานะจัดส่ง+ชำระเงิน merge เต็มความกว้าง (grid-column 1 / -1)
      // เพื่อไม่ให้ข้อความสถานะดันเลขพัสดุ/ปุ่มไปทางขวา
      title: 'เลขพัสดุ',
      dataIndex: 'tracking_number',
      onCell: topAlignCell,
      render: (tn, record) => {
        const printable = canPrint(record);
        // ค่าที่ copy — self ใช้เลขจริงถ้ามี ไม่งั้น fallback ไปรหัส BB (คนละเงื่อนไขจาก printable
        // เพราะพอกรอกเลขจริงแล้วซ่อนปุ่มพิมพ์ แต่ยัง copy เลขจริงได้ตามปกติ)
        const copyValue = record.is_self ? (record.self_tracking_number || record.tracking_number) : record.tracking_number;
        // โชว์รหัส BB ต่อท้ายสถานะจัดส่ง เฉพาะตอนที่ยังไม่ได้กรอกเลขพัสดุจริง
        const showBbInStatus = record.is_self && record.self_tracking_number == null && !!tn;
        // แถบเตรียมจัดส่ง/ระหว่างจัดส่ง: ตัดสถานะจัดส่งทิ้ง เหลือแค่รหัส BB
        const showShippingStatus = !!record.status && !(bbCodeOnly && showBbInStatus);
        return (
          <div
            style={{
              display: 'grid',
              // เลขพัสดุ | (สถานะพิมพ์) | คัดลอก | (พิมพ์)
              gridTemplateColumns: hidePrintControls
                ? 'minmax(0, 1fr) auto'
                : 'minmax(0, 1fr) auto auto auto',
              columnGap: 8,
              rowGap: 4,
              alignItems: 'center',
            }}
          >
            {/* บรรทัด 1 ซ้าย: เลขพัสดุ + COD */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 22, minWidth: 0 }}>
              {record.is_self ? (
                record.self_tracking_number != null ? (
                  <>
                    <Typography.Text style={{ fontSize: 13 }}>{record.self_tracking_number}</Typography.Text>{' '}
                    <Tag style={{ fontSize: 8, color: 'gray' }}>ส่งเอง</Tag>
                  </>
                ) : (
                  // ยังไม่ได้กรอกเลขพัสดุจริงจากไปรษณีย์ — รหัส BB ไปโชว์ต่อท้ายสถานะจัดส่งแทน (บรรทัด 2)
                  <Tag color="orange" style={{ fontSize: 10 }}>ยังไม่ได้บันทึกเลขพัสดุ</Tag>
                )
              ) : tn ? (
                <Typography.Text style={{ fontSize: 13 }}>{tn}</Typography.Text>
              ) : (
                <Tag color="orange" style={{ fontSize: 10 }}>⏳ รอดำเนินการ</Tag>
              )}
              {/* ข้อความ COD — payment_type เป็นข้อมูลระดับออเดอร์ เหมือนกันทุก tracking row */}
              {record.payment_type === 'COD' && (
                <Typography.Text style={{ fontSize: 12, color: '#ff4d4f' }}>COD</Typography.Text>
              )}
            </div>

            {/* บรรทัด 1 ขวา: สถานะพิมพ์ (ซ่อนเมื่อ hidePrintControls) */}
            {!hidePrintControls && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 22 }}>
                {printable
                  ? (record.is_print
                    ? <Tag color="green" style={{ fontSize: 10, margin: 0 }}>พิมพ์แล้ว</Tag>
                    : <Tag color="default" style={{ fontSize: 10, margin: 0 }}>ยังไม่พิมพ์</Tag>)
                  : null}
              </div>
            )}

            {/* บรรทัด 1 ขวา: (แก้ไขเลขส่งเอง) + คัดลอก */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 22 }}>
              {allowEditSelfTracking && record.is_self && record.self_tracking_number != null && record.retail_order_item_id ? (
                <Tooltip title="แก้ไขเลขพัสดุ">
                  <EditOutlined
                    style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                    onClick={() => openEditSelf(record.retail_order_item_id!)}
                  />
                </Tooltip>
              ) : null}
              {copyValue ? (
                <Tooltip title="คัดลอก">
                  <CopyOutlined
                    style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                    onClick={() => handleCopy(copyValue)}
                  />
                </Tooltip>
              ) : null}
            </div>

            {/* บรรทัด 1 ขวา: พิมพ์ Label (ซ่อนเมื่อ hidePrintControls) */}
            {!hidePrintControls && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 22 }}>
                {printable ? (
                  <Tooltip title="พิมพ์ Label นี้">
                    <PrinterOutlined
                      style={{ color: '#52c41a', cursor: 'pointer', fontSize: 13 }}
                      onClick={() => printSingle(record)}
                    />
                  </Tooltip>
                ) : null}
              </div>
            )}

            {/* บรรทัด 2: สถานะจัดส่ง + สถานะชำระเงิน — merge เต็มความกว้างใต้เลขพัสดุและปุ่ม
                3 สถานะจาก webhook จริงของ tracking แถวนี้: "ชำระแล้ว" (is_cod_settled) /
                "ชำระเงินไม่สำเร็จ" (status=canceled/returned เก็บเงินไม่ได้จริง) /
                "ยังไม่ได้ชำระเงิน" (ยังไม่มีผล webhook เลย) แสดงเป็นข้อความธรรมดา ไม่ทำเป็น badge/Tag ตามที่ขอ */}
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'nowrap',
              }}
            >
              {showShippingStatus && record.status ? (
                // แสดงเฉพาะหมวด SHIPPING_ACTIVITY_STATUS กว้างๆ (กำลังจัดส่ง/จัดส่งสำเร็จ/...)
                // ไม่ใช้ status_label จาก webhook เพราะมักมีชื่อสถานีต่อท้าย เช่น "ระหว่างจัดส่ง : สถานีกรุงเทพฯ"
                // ส่งเองที่ยังไม่ได้กรอกเลขพัสดุจริง — ต่อท้ายด้วยรหัส BB เช่น "กำลังจัดส่ง : BB1234567890"
                <Typography.Text
                  style={{
                    fontSize: 12,
                    color: SHIPPING_STATUS_COLOR[record.status] ?? '#999999',
                    minWidth: 0,
                    flex: '0 1 auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {SHIPPING_STATUS_LABEL_TH[record.status] ?? record.status}
                  {showBbInStatus ? ` : ${tn}` : ''}
                </Typography.Text>
              ) : showBbInStatus ? (
                <Typography.Text style={{ fontSize: 12, color: '#999999', whiteSpace: 'nowrap' }}>
                  {tn}
                </Typography.Text>
              ) : null}
              {record.payment_type === 'COD' && (() => {
                // เก็บเงินไม่ได้จริง (webhook ตีเป็น canceled/returned — ดู isFailedEvent ฝั่ง
                // api-external.service.ts) ต้องแยกจาก "ยังไม่มีผล webhook เลย" ไม่ให้ปนกัน
                const codFailed = record.status === 'canceled' || record.status === 'returned';
                const label = record.is_cod_settled
                  ? 'ชำระแล้ว'
                  : codFailed
                    ? 'เรียกเก็บเงินไม่สำเร็จ'
                    : 'ยังไม่ได้ชำระเงิน';
                const color = record.is_cod_settled ? '#52c41a' : codFailed ? '#ff4d4f' : '#999999';
                return (
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      color,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {label}
                  </Typography.Text>
                );
              })()}
            </div>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (rows.length === 0) {
    if (hideEmptyState) return null;
    return <div style={{ padding: '8px 0', color: '#999', fontSize: 12 }}>ยังไม่มีข้อมูลเลขพัสดุ</div>;
  }

  // Group by product_name + sku_description
  const productKeys = Array.from(new Set(rows.map((r) => `${r.product_name}||${r.sku_description ?? ''}`)));
  // ไม่มีแถวไหนพิมพ์ได้เลย (เช่น ส่งเองที่กรอกเลขจริงครบแล้ว) → ซ่อนปุ่มพิมพ์ทั้งหมด
  const hasPrintable = rows.some(canPrint);

  return (
    <>
      {/* ---- Header: เลขพัสดุ (ซ้าย) + ปุ่ม Print (ขวา, ซ่อนเมื่อ hidePrintControls) ---- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Typography.Text strong style={{ fontSize: 14 }}>เลขพัสดุ</Typography.Text>
        {!hidePrintControls && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {selectedKeys.length > 0 && (
              <Button
                size="small"
                style={{ fontSize: 12 }}
                type="primary"
                icon={<PrinterOutlined />}
                loading={mergeLoading}
                onClick={printSelected}
              >
                พิมพ์ที่เลือก ({selectedKeys.length})
              </Button>
            )}
            {hasPrintable && (
              <Button
                size="small"
                style={{ fontSize: 12 }}
                icon={<PrinterOutlined />}
                loading={mergeLoading}
                onClick={printAll}
              >
                พิมพ์ทั้งหมด
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ---- ตารางแยก SKU — scroll เฉพาะส่วนนี้ ---- */}
      <div style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 2 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {productKeys.map((pk) => {
          const [productName, skuDesc] = pk.split('||');
          const productRows = rows.filter(
            (r) => r.product_name === productName && (r.sku_description ?? '') === skuDesc
          );
          const skuAllKeys = productRows.filter(canPrint).map((r) => r.key);
          const allSkuSelected = skuAllKeys.length > 0 && skuAllKeys.every((k) => selectedKeys.includes(k));

          return (
            <div key={pk}>
              {/* Header สินค้า */}
              <div style={{
                background: '#f5f5f5',
                padding: '5px 8px',
                borderRadius: '6px 6px 0 0',
                borderBottom: '1px solid #e8e8e8',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                {/* ซ้าย: Checkbox (เฉพาะไม่ hidePrintControls) + ชื่อสินค้า */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {!hidePrintControls && (
                    <Checkbox
                      checked={allSkuSelected}
                      disabled={skuAllKeys.length === 0}
                      indeterminate={!allSkuSelected && skuAllKeys.some((k) => selectedKeys.includes(k))}
                      onChange={(e) => {
                        setSelectedKeys((prev) =>
                          e.target.checked
                            ? Array.from(new Set([...prev, ...skuAllKeys]))
                            : prev.filter((k) => !skuAllKeys.includes(k))
                        );
                      }}
                    />
                  )}
                  <Typography.Text strong style={{ fontSize: 12 }}>
                     {productName}{skuDesc ? ` : ${skuDesc}` : ''}
                  </Typography.Text>
                </div>
                {/* ขวา: ปุ่มพิมพ์ทั้ง SKU (ซ่อนเมื่อ hidePrintControls หรือ SKU นี้ไม่มีแถวที่พิมพ์ได้) */}
                {!hidePrintControls && skuAllKeys.length > 0 && (
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    loading={mergeLoading}
                    onClick={() => printBySku(skuDesc || productName, productRows)}
                  >
                  </Button>
                )}
              </div>
              <Table<TrackingRow>
                columns={columns}
                dataSource={productRows}
                pagination={false}
                size="small"
                showHeader={false}
                style={{ borderRadius: '0 0 6px 6px', overflow: 'hidden' }}
              />
            </div>
          );
        })}
      </div>
      </div>{/* end scroll container */}

      {/* ---- PDF Preview Modal ---- */}
      <Modal
        title={`Label — ${pdfTitle}`}
        open={pdfOpen}
        onCancel={closePdfPreview}
        footer={null}
        width={560}
        style={{ top: 24 }}
        destroyOnClose
      >
        <PdfViewerModal
          pdfUrl={pdfUrl}
          title={pdfTitle}
          onAfterPrint={afterPrint}
          showAllPagesOption
          defaultViewMode="all"
        />
      </Modal>

      {/* ---- แก้ไขเลขพัสดุส่งเอง (POST /order-tracks/self-tracking เขียนทับทุกกล่องของรายการ) ---- */}
      <Modal
        title="แก้ไขเลขพัสดุ (ส่งเอง)"
        open={!!editFirst}
        onCancel={() => setEditSelfItemId(null)}
        footer={null}
        width={480}
        maskClosable={!editSaving}
        closable={!editSaving}
        destroyOnClose
      >
        {editFirst && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
              <Typography.Text strong>
                {editFirst.product_name}{editFirst.sku_description ? ` : ${editFirst.sku_description}` : ''}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ flexShrink: 0 }}>จำนวน {editPieces.length}</Typography.Text>
            </div>
            <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
              เลขที่บันทึกจะใช้แทนเลขเดิมของทุกชิ้นในรายการนี้
            </Typography.Paragraph>
            {selfBrands.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : (
              <SelfTrackingForm
                rows={[{
                  order_item_id: editFirst.retail_order_item_id!,
                  product_name: editFirst.product_name,
                  ship_qty: editPieces.length,
                }]}
                brands={selfBrands}
                initialBrandKey={(() => {
                  // self_transport_brand_id อาจเก็บเป็น uuid หรือ id — แปลงเป็น key ที่ dropdown ใช้
                  const b = selfBrands.find((x) => x.uuid === editFirst.self_transport_brand_id || x.id === editFirst.self_transport_brand_id);
                  return b ? getSelfShippingBrandKey(b) : undefined;
                })()}
                initialNumbers={piecesToTrackingNumbers(editPieces.map((r) => r.self_tracking_number ?? ''))}
                onSavingChange={setEditSaving}
                onSaved={() => window.dispatchEvent(new Event('forceRefreshOrderTable'))}
                onDone={() => setEditSelfItemId(null)}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default TrackingNumberPanel;
